import { buildScale, BREAKDOWN_COLUMN_HEIGHT as COLUMN_HEIGHT } from "../lib/breakdown-scale";

const BAR_MIN_HEIGHT = 3;
const STAGGER_MS = 60;
const GROW_MS = 420;
const LABEL_DELAY_MS = 160;

const BAR_POSITIVE = "#4FA97A";
const BAR_NEGATIVE = "#E8837C";
const VALUE_POSITIVE = "#3FA971";
const VALUE_NEGATIVE = "#E0483F";
const INK = "#21133a";

export type BreakdownColumn = {
  id: string;
  name: string;
  delta: number;
};

type GmvBreakdownChartProps = {
  columns: BreakdownColumn[];
  netChange: number;
  hoveredId: string | null;
  selectedId: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
  formatDelta: (value: number) => string;
  animationKey: string;
};

export function GmvBreakdownChart({
  columns,
  netChange,
  hoveredId,
  selectedId,
  onHover,
  onSelect,
  formatDelta,
  animationKey,
}: GmvBreakdownChartProps) {
  const { ticks, y } = buildScale(
    columns.map((column) => column.delta),
    netChange,
  );
  const zero = y(0);

  const clearHover = (id: string) => onHover(hoveredId === id ? null : hoveredId);

  return (
    <div key={animationKey} className="overflow-x-auto">
      <div className="flex min-w-[640px] items-start gap-3 pt-2.5">
        <div className="relative w-[64px] shrink-0" style={{ height: COLUMN_HEIGHT }}>
          {ticks.map((tick) => (
            <span
              key={tick}
              style={{ top: y(tick), color: Math.abs(tick) < 0.001 ? `${INK}a6` : `${INK}80` }}
              className="absolute right-0 -translate-y-1/2 text-[12px] leading-5 font-medium whitespace-nowrap tabular-nums"
            >
              {Math.abs(tick) < 0.001 ? "0" : formatDelta(tick)}
            </span>
          ))}
        </div>

        <div
          className="relative grid min-w-0 flex-1 items-stretch gap-2.5"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
        >
          {ticks.map((tick) => (
            <div
              key={tick}
              aria-hidden="true"
              style={{ top: y(tick), backgroundColor: Math.abs(tick) < 0.001 ? `${INK}26` : `${INK}14` }}
              className="pointer-events-none absolute inset-x-0 z-10 h-px"
            />
          ))}

          {columns.map((column, index) => {
            const positive = column.delta >= 0;
            const edge = y(column.delta);
            const height = Math.max(Math.abs(zero - edge), BAR_MIN_HEIGHT);
            const lit = !hoveredId || hoveredId === column.id;

            return (
              <button
                key={column.id}
                type="button"
                aria-expanded={selectedId === column.id}
                onMouseEnter={() => onHover(column.id)}
                onMouseLeave={() => clearHover(column.id)}
                onFocus={() => onHover(column.id)}
                onBlur={() => clearHover(column.id)}
                onClick={() => onSelect(column.id)}
                className="flex min-w-0 cursor-pointer flex-col gap-4 text-left"
              >
                <div className="relative w-full" style={{ height: COLUMN_HEIGHT }}>
                  <span aria-hidden="true" className="absolute inset-0 rounded-[6px] bg-[#21133a]/[0.03]" />

                  <span
                    aria-hidden="true"
                    style={{
                      backgroundColor: positive ? BAR_POSITIVE : BAR_NEGATIVE,
                      top: positive ? edge : zero,
                      height,
                      transformOrigin: positive ? "bottom" : "top",
                      animation: `bar-grow ${GROW_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1) ${index * STAGGER_MS}ms both`,
                      outline: selectedId === column.id ? `1.5px solid ${INK}` : undefined,
                    }}
                    className={[
                      "bar-grow absolute inset-x-0 z-20 transition-opacity duration-200",
                      positive ? "rounded-t-[4px]" : "rounded-b-[4px]",
                      lit ? "" : "opacity-30",
                    ].join(" ")}
                  />

                  <span
                    style={{
                      top: positive ? Math.max(edge - 40, 0) : zero + height + 6,
                      animation: `fade-in-up 380ms ease-out ${index * STAGGER_MS + LABEL_DELAY_MS}ms both`,
                    }}
                    className="absolute inset-x-0 z-20 flex flex-col items-center gap-0.5"
                  >
                    <span
                      className="text-[12px] leading-4 font-semibold tabular-nums"
                      style={{ color: positive ? VALUE_POSITIVE : VALUE_NEGATIVE }}
                    >
                      {positive ? "+" : "−"}
                      {formatDelta(Math.abs(column.delta))}
                    </span>
                  </span>
                </div>

                <p
                  className="truncate text-[12px] leading-4 transition-colors duration-200"
                  style={{ color: lit ? `${INK}8c` : `${INK}59` }}
                >
                  {column.name}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
